'use client';

import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

type SearchCommandContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const SearchCommandContext = createContext<SearchCommandContextValue | null>(
  null
);

/**
 * Shared open/closed state for the global recipe/ingredient search palette,
 * so the header trigger (desktop) and NavigationBar trigger (mobile) open the
 * same dialog instance instead of each mounting their own. Also wires the
 * ⌘K/Ctrl+K shortcut globally.
 */
export const SearchCommandProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const value = useMemo(() => ({ open, setOpen }), [open]);

  return (
    <SearchCommandContext.Provider value={value}>
      {children}
    </SearchCommandContext.Provider>
  );
};

export function useSearchCommand(): SearchCommandContextValue {
  const ctx = useContext(SearchCommandContext);
  if (!ctx) {
    throw new Error(
      'useSearchCommand must be used within a SearchCommandProvider'
    );
  }
  return ctx;
}
